import { addMinutes } from 'date-fns/addMinutes';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';

import { dataViewI18nT } from '../../../core/i18n.js';
import type { CalendarSingleView } from '../calendar-view-manager.js';
import type { CalendarEvent } from './calendar-view-types.js';
import { guessTimeZone } from './calendar-view-utils.js';

export const CALENDAR_TIMED_EVENT_MINUTES = 15;

export function createCalendarEventRange(
  view: CalendarSingleView,
  day: Date,
  startMinute: number,
  endMinute: number,
  options: { allDay?: boolean } = {}
): CalendarEvent | undefined {
  if (view.readonly$.value) return undefined;
  view.ensureCalendarColumns();
  const rowId = view.rowAdd('end');
  if (!rowId) return undefined;

  const normalizedStart = Math.max(0, Math.min(23 * 60 + 45, startMinute));
  const normalizedEnd = Math.max(
    normalizedStart + CALENDAR_TIMED_EVENT_MINUTES,
    endMinute
  );
  const start = addMinutes(startOfDay(day), normalizedStart);
  const end = addMinutes(startOfDay(day), normalizedEnd);

  writeCalendarCellValue(
    view,
    rowId,
    view.startDateCol$.value,
    start.getTime()
  );
  writeCalendarCellValue(view, rowId, view.endDateCol$.value, end.getTime());
  writeCalendarCellValue(
    view,
    rowId,
    view.allDayCol$.value,
    options.allDay === true
  );
  writeCalendarCellValue(view, rowId, view.timezoneCol$.value, guessTimeZone());
  writeCalendarCellValue(view, rowId, view.reminderCol$.value, '15m');
  writeCalendarCellValue(
    view,
    rowId,
    view.mainProperties$.value.titleColumn,
    dataViewI18nT('calendar.event.newTitle')
  );
  return readCalendarEvent(view, rowId);
}

export function readCalendarString(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined
): string {
  if (!propertyId) return '';
  return view.cellGetOrCreate(rowId, propertyId).stringValue$.value ?? '';
}

export function readCalendarEvent(
  view: CalendarSingleView,
  rowId: string,
  options: { includeDetails?: boolean } = {}
): CalendarEvent | undefined {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return undefined;

  const startRaw = view.cellGetOrCreate(rowId, startCol).jsonValue$.value;
  const endRaw = view.cellGetOrCreate(rowId, endCol).jsonValue$.value;
  if (typeof startRaw !== 'number' || typeof endRaw !== 'number') {
    return undefined;
  }

  const startAt = new Date(startRaw);
  const endAt = new Date(endRaw);
  if (
    !Number.isFinite(startAt.getTime()) ||
    !Number.isFinite(endAt.getTime())
  ) {
    return undefined;
  }

  const titleCol = view.mainProperties$.value.titleColumn;
  const allDayCol = view.allDayCol$.value;
  const event: CalendarEvent = {
    allDay: allDayCol
      ? view.cellGetOrCreate(rowId, allDayCol).jsonValue$.value === true
      : false,
    attendeeCount: 0,
    calendarId: readCalendarString(view, rowId, view.calendarIdCol$.value),
    recurrence: readCalendarString(view, rowId, view.recurrenceCol$.value),
    recurrenceExdate: readCalendarString(
      view,
      rowId,
      view.recurrenceExdateCol$.value
    ),
    recurrenceId: readCalendarString(view, rowId, view.recurrenceIdCol$.value),
    timezone: readCalendarString(view, rowId, view.timezoneCol$.value),
    attendees: '',
    description: '',
    endAt: isBefore(endAt, startAt) ? startAt : endAt,
    locationPhysical: '',
    locationVirtual: '',
    reminder: '',
    rowId,
    startAt,
    status: '',
    title:
      (titleCol
        ? view.cellGetOrCreate(rowId, titleCol).stringValue$.value
        : undefined) || dataViewI18nT('calendar.event.untitled'),
    viewId: view.id,
    visibility: '',
  };
  return options.includeDetails === false
    ? event
    : hydrateCalendarEventDetails(view, event);
}

export function hydrateCalendarEventDetails(
  view: CalendarSingleView,
  event: CalendarEvent
): CalendarEvent {
  if (event.externalReadonly) return event;
  const rowId = event.rowId;
  return {
    ...event,
    attendees: readCalendarString(view, rowId, view.attendeesCol$.value),
    description: readCalendarString(view, rowId, view.descriptionCol$.value),
    locationPhysical: readCalendarString(
      view,
      rowId,
      view.locationPhysicalCol$.value
    ),
    locationVirtual: readCalendarString(
      view,
      rowId,
      view.locationVirtualCol$.value
    ),
    reminder: readCalendarString(view, rowId, view.reminderCol$.value),
    status: readCalendarString(view, rowId, view.statusCol$.value),
    visibility: readCalendarString(view, rowId, view.visibilityCol$.value),
  };
}

export function writeCalendarCellValue(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined,
  value: unknown
): boolean {
  if (!propertyId) return false;
  const property = view.propertyGetOrCreate(propertyId);
  if (property.type$.value === 'select' && typeof value === 'string') {
    property.valueSetFromString(rowId, value);
  } else {
    view.cellGetOrCreate(rowId, propertyId).jsonValueSet(value);
  }
  return true;
}
